import { useEffect, type ReactNode } from 'react'

type FaqItem = {
  q: string
  a: ReactNode
}

type FaqSection = {
  id: string
  title: string
  items: FaqItem[]
}

const sections: FaqSection[] = [
  {
    id: 'process-selection',
    title: 'Process & Selection',
    items: [
      {
        q: 'Why was KEXP chosen over the other two finalists (Crossroads, LOVEHOUSE)?',
        a: (
          <p>
            All three finalist teams brought thoughtful, ambitious visions, and we are grateful for the
            extreme care and detail each put into their proposals. SF Heritage&apos;s Board selected KEXP
            based on its organizational strength, the clarity and appropriateness of its program, its
            partnership with SF Legacy Business Blue Bear Music School, and the financial feasibility of
            their proposal. SF Heritage believes that KEXP will be a good and long-term steward of this
            historic site.
          </p>
        ),
      },
      {
        q: 'How was community feedback incorporated into the decision?',
        a: (
          <p>
            Feedback gathered through written comments, tours of the Doolan-Larson House, conversations
            with the neighborhood, and the well-attended public forum held in April 2026 directly informed
            the Board&apos;s deliberations. In essence, we understood that the local community wanted to
            celebrate the importance of the site, but give it a new life, to make the famous intersection
            a place not simply for visitors, but for the neighbors, including families. We believe that
            KEXP will achieve this.
          </p>
        ),
      },
      {
        q: 'Who made the final decision?',
        a: (
          <p>
            The selection followed a structured, multi-stage process. The criteria were published in the
            RFP and responses evaluated by SF Heritage&apos;s Board of Directors in consultation with an
            independent Advisory Committee. The Board&apos;s vote took place at a formal meeting of the
            board, consistent with our governance practices. The decision was unanimous.
          </p>
        ),
      },
      {
        q: 'Will concepts (or elements of them) from the other proposals be considered in any way going forward?',
        a: (
          <>
            <p>
              KEXP will oversee programming and operations; together we will explore incorporating
              additional elements, as appropriate. One of the most compelling aspects of KEXP&apos;s
              proposal was its programmatic clarity and the greater potential for longevity.
            </p>
            <p>
              We are nonetheless grateful for the exceptional work of all the teams, including those who
              submitted concept proposals. They helped us build a constituency for the site and deepened
              our understanding of its potential.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: 'out-of-town',
    title: 'Out-of-Town Organization',
    items: [
      {
        q: 'KEXP is based in Seattle — why not a San Francisco-based team for a San Francisco landmark?',
        a: (
          <p>
            We evaluated all three finalists on the strength and feasibility of their vision rather than
            geography, and KEXP&apos;s proposal aligned best with our criteria. Although the Doolan-Larson
            House is a designated San Francisco Landmark, the site resonates around the world. KEXP has an
            international reach and its approach to programming is very much in keeping with the
            &ldquo;spirit of the place&rdquo;.
          </p>
        ),
      },
      {
        q: 'Does KEXP have any existing presence, staff, or history in the Bay Area? What ties do they have to Haight-Ashbury?',
        a: (
          <>
            <p>
              KEXP&apos;s programming has long featured Bay Area artists and stories, and the organization
              has been building direct relationships with neighborhood stakeholders throughout this
              process. Establishing a genuine, on-the-ground local presence — not a satellite outpost —
              is a top priority for KEXP.
            </p>
            <p>
              KEXP currently employs four full-time staff members, four on-air hosts, and several
              flex-time event and community engagement staff in the Bay Area. In addition, KEXP&apos;s
              Board of Directors includes four Bay Area residents, with more likely to be added in the
              coming year. KEXP currently maintains offices and broadcast operations at KQED&apos;s
              headquarters in the Mission District.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: 'historic-preservation',
    title: 'Historic Preservation',
    items: [
      {
        q: "What will physically change about the building? Will character-defining features (Norm Larson's salon rooms, storefronts, facade) be altered?",
        a: (
          <p>
            Any physical changes to the buildings will be guided first by preservation of the
            building&apos;s historic character. KEXP&apos;s design team includes historic preservation
            specialists, and specific plans will be developed in close collaboration with SF Heritage.
          </p>
        ),
      },
      {
        q: "Will the Secretary of the Interior's Standards be followed, and who verifies compliance?",
        a: (
          <p>
            All work will comply with the Secretary of the Interior&apos;s Standards for Rehabilitation.
            SF Heritage will maintain ongoing oversight of preservation compliance throughout design and
            construction.
          </p>
        ),
      },
      {
        q: 'Is KEXP pursuing Historic Tax Credits, and does that impose any additional preservation review (state/federal)?',
        a: (
          <p>
            The project intends to pursue Federal Historic Preservation Tax Credits, which brings
            additional layers of state and federal preservation review, reinforcing our shared commitment
            to protecting the building&apos;s historic fabric.
          </p>
        ),
      },
      {
        q: 'Will the public be able to weigh in on design details before construction begins?',
        a: (
          <p>
            We anticipate further opportunities for community feedback as design plans develop, similar to
            the public forum held during the RFP process.
          </p>
        ),
      },
      {
        q: "What happens to Norm Larson's personal belongings, if any remain in the house?",
        a: (
          <p>
            SF Heritage remains committed to honoring Norm Larson&apos;s legacy. Any decisions regarding
            remaining personal items will be made thoughtfully and in keeping with the spirit of his
            bequest.
          </p>
        ),
      },
    ],
  },
  {
    id: 'neighborhood-impact',
    title: 'Neighborhood Impact',
    items: [
      {
        q: 'Will this bring more noise, late-night crowds, or nightlife-style activity?',
        a: (
          <p>
            Operating hours and sound management will comply with San Francisco ordinances. KEXP&apos;s
            proposed programming emphasizes daytime and early-evening community and cultural activity
            rather than a late-night model.
          </p>
        ),
      },
      {
        q: 'What are the plans for foot traffic, parking, and loading/unloading during events?',
        a: (
          <p>
            Traffic, parking, and loading logistics will be addressed through the entitlement and
            permitting process, which includes opportunities for neighborhood input.
          </p>
        ),
      },
      {
        q: 'Will there be live outdoor broadcasts or amplified sound audible from the street?',
        a: (
          <p>
            Any outdoor or amplified programming will go through the appropriate permitting and community
            notification process.
          </p>
        ),
      },
      {
        q: 'How will construction disruption (noise, dust, street closures) be managed and communicated?',
        a: (
          <p>
            Construction will bring some temporary disruption. KEXP and SF Heritage are committed to
            minimizing the impacts to the extent possible and will provide advance notice, a dedicated
            point-of-contact, and regular progress updates throughout the build-out.
          </p>
        ),
      },
      {
        q: 'Is there a risk of increased short-term rentals, tour buses, or "tourist trap" dynamics on the block?',
        a: (
          <p>
            This project is intended to strengthen, not overwhelm, the everyday character of
            Haight-Ashbury. Large-scale tour bus or short-term-rental-driven visitation is not part of
            the plan, and furthermore is regulated by the City.
          </p>
        ),
      },
    ],
  },
  {
    id: 'community-access',
    title: 'Community Access & Affordability',
    items: [
      {
        q: 'Will programming be free or ticketed? Will locals get discounted or priority access?',
        a: (
          <p>
            KEXP&apos;s proposal includes a mix of free and ticketed programming, with an emphasis on
            keeping a substantial share of activities accessible to the community at no or low cost.
          </p>
        ),
      },
      {
        q: 'Is this primarily a members-only or donor-driven institution, or genuinely open to walk-in neighbors?',
        a: (
          <p>
            The intent is to create a welcoming public, walk-in space for the neighborhood, and visitors,
            supported in part by membership and philanthropy — much like public radio operates. In
            Seattle, at the primary KEXP location, a community space is open to the public daily.
          </p>
        ),
      },
      {
        q: 'Will longtime Haight-Ashbury residents — not just donors or tourists — feel welcome and represented in programming?',
        a: (
          <p>
            Community input in programming decisions is a priority for KEXP, it is at the heart of their
            &ldquo;listener-powered&rdquo; programming.
          </p>
        ),
      },
      {
        q: 'What is the nature of the partnership between the Blue Bear School of Music and KEXP?',
        a: (
          <p>
            For fifty-five years, The Blue Bear School of Music has provided music education for local
            youth and adults at several campuses across the city. Details on Blue Bear program design and
            access for the Doolan-Larson site will be shared as planning continues.
          </p>
        ),
      },
      {
        q: 'Will there be programming in languages other than English, and for underserved neighborhood populations?',
        a: (
          <p>
            KEXP does offer programming in Spanish: The <em>El Sonido: Cancioneros</em> podcast and{' '}
            <em>La Musica Importa</em> email newsletter, as well as many <em>Live on KEXP</em> sessions
            hosted/presented/with interviews in Spanish. They regularly present music and live
            performances featuring artists from a wide range of global communities, traditions, and
            languages through their daily variety mix, weekly specialty shows, and <em>Live on KEXP</em>{' '}
            videos online.
          </p>
        ),
      },
    ],
  },
  {
    id: 'local-business',
    title: 'Local Business & Economic Impact',
    items: [
      {
        q: 'Will this compete with existing local music venues, cafés, or retailers, or complement them?',
        a: (
          <p>
            The intent is to complement, not compete with, the existing local enterprises — drawing
            visitors into the neighborhood in ways that support neighboring businesses.
          </p>
        ),
      },
      {
        q: "What's the plan for the ground-floor retail/café space — chain tenant or local operator?",
        a: (
          <p>
            Plans for any ground-floor commercial space will prioritize local, neighborhood-serving
            operators to the extent feasible, consistent with the project&apos;s community-first goals.
          </p>
        ),
      },
      {
        q: 'Will the project create local jobs, and at what wage/level (entry-level vs. specialized)?',
        a: (
          <p>
            The project is expected to create both entry-level and specialized positions, with a
            commitment to local hiring wherever possible. Specific staffing plans will be finalized once
            the program is fully developed.
          </p>
        ),
      },
      {
        q: 'Could increased visitation drive up rents or contribute to displacement pressure in the corridor?',
        a: (
          <p>
            We share the community&apos;s concern about displacement pressure in the neighborhood. This
            project&apos;s goal is to strengthen the existing local economy, and we&apos;ll continue
            engaging with neighborhood partners on this issue as it develops.
          </p>
        ),
      },
    ],
  },
  {
    id: 'governance',
    title: 'Governance, Lease & Financial Transparency',
    items: [
      {
        q: 'Who owns the building going forward — does SF Heritage retain ownership? Is this a long-term ground lease?',
        a: (
          <p>
            San Francisco Heritage will retain ownership of the Doolan-Larson House, consistent with its
            obligations under the Larson Estate covenant. KEXP will operate under a long-term lease,
            currently in negotiation.
          </p>
        ),
      },
      {
        q: 'What are the lease terms (length, rent, renewal options)? Is any of this public?',
        a: (
          <p>
            Key lease terms will be shared with the public, as appropriate, once negotiations are
            finalized.
          </p>
        ),
      },
      {
        q: "How is SF Heritage protecting its covenant obligations under the Larson Estate trust if KEXP controls day-to-day operations?",
        a: (
          <p>
            SF Heritage retains ownership and ongoing oversight of preservation standards and covenant
            compliance, with regular reporting and review built into the operating agreement with KEXP.
          </p>
        ),
      },
      {
        q: "What happens if KEXP's fundraising campaign falls short, or if the organization faces financial trouble down the line?",
        a: (
          <p>
            KEXP brings an established financial track record, including a successful capital campaign
            for its Seattle headquarters and a robust existing donor base. The lease and operating
            agreement will include safeguards to protect SF Heritage&apos;s interests and the building
            itself, but we are optimistic that KEXP can accomplish what it is proposing.
          </p>
        ),
      },
      {
        q: 'Is public/city money involved in this project in any way, and if so, how much and for what?',
        a: (
          <p>
            We will be transparent about any public funding sources as they are finalized. At this stage,
            the capital plan is centered on a philanthropic campaign led by KEXP.
          </p>
        ),
      },
    ],
  },
  {
    id: 'timeline',
    title: 'Timeline & Disruption',
    items: [
      {
        q: 'When does construction start, and how long will it take?',
        a: (
          <p>
            A detailed construction timeline will be finalized following lease execution and the
            entitlement process. We&apos;ll share projected milestones as soon as they&apos;re confirmed.
          </p>
        ),
      },
      {
        q: 'Will the building be closed to the public entirely during construction, or partially accessible?',
        a: (
          <p>
            Specific plans for public access during construction are still being developed and will be
            communicated well in advance of any closures.
          </p>
        ),
      },
      {
        q: "What's the realistic opening date, and how will delays be communicated?",
        a: (
          <p>
            SF Heritage will communicate realistic timelines, impacts, and key changes should there be
            any as the project moves from planning into construction.
          </p>
        ),
      },
      {
        q: 'What happens to the storefronts/tenants currently in the building (if any) during the transition?',
        a: (
          <p>
            Transition plans for existing tenants or storefronts will be handled thoughtfully and
            communicated directly to those affected as early as possible. We anticipate that the tenants
            will be able to stay until close to the start of construction, and that the preconstruction
            period (including lease negotiations, entitlements, design and permitting) will take more
            than a year or more to complete, and that the tenants will hopefully stay through that
            period.
          </p>
        ),
      },
    ],
  },
  {
    id: 'mission',
    title: 'Mission & Cultural Affinity',
    items: [
      {
        q: 'Does a radio station "fit" the spirit of a house tied to the Summer of Love and counterculture history?',
        a: (
          <p>
            Radio has deep roots in the counterculture movements that this neighborhood helped define,
            from underground FM broadcasting to community storytelling. KEXP&apos;s freeform,
            artist-driven model is very much in that same spirit of independent, community-centered
            media.
          </p>
        ),
      },
      {
        q: "Is this capitalizing on the neighborhood's cultural narrative rather than preserving it?",
        a: (
          <p>
            Our shared goal is to actively preserve and amplify Haight-Ashbury&apos;s cultural legacy,
            not redefine or commodify it. We see this as a long-term investment in keeping that legacy
            vibrant and accessible.
          </p>
        ),
      },
      {
        q: "Is this just a real estate deal dressed up as a cultural mission?",
        a: (
          <p>
            This project exists because of a charitable bequest specifically intended to preserve and
            activate this building for public cultural benefit, not as a commercial transaction. The RFP
            criteria and covenant obligations were structured around that public mission from the outset.
          </p>
        ),
      },
      {
        q: "What guarantees exist that this doesn't become another tech-adjacent, donor-class amenity rather than a genuine community space?",
        a: (
          <p>
            The core requirements of the Larson Estate covenant — public-facing programming, community
            access, and preservation in perpetuity — are binding obligations, not aspirations. SF
            Heritage and KEXP will remain true to those commitments.
          </p>
        ),
      },
    ],
  },
]

const FAQ = () => {
  useEffect(() => {
    document.title = 'FAQ - Haight & Ashbury'
  }, [])

  return (
    <div>
      <section className="page-hero">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-0">Frequently Asked Questions</h1>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <nav aria-label="FAQ sections" className="mb-12">
              <ul className="flex flex-wrap gap-x-4 gap-y-2 text-base">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="text-haight-navy underline underline-offset-4 hover:text-black min-h-11 inline-flex items-center"
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="space-y-16">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-36 md:scroll-mt-48 space-y-8">
                  <h2 className="text-haight-navy text-[clamp(1.3125rem,3.375vw,36.75px)]">{section.title}</h2>
                  <div className="space-y-8">
                    {section.items.map((item) => (
                      <div key={item.q} className="space-y-3">
                        <h3 className="text-[1.15rem] md:text-[1.25rem] text-haight-navy leading-snug">
                          {item.q}
                        </h3>
                        <div className="space-y-4 text-lg text-gray-800 leading-relaxed">{item.a}</div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default FAQ
